import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ReceiptText,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  http,
  rupiah,
  guestToken,
} from "@/lib/api";
import ThemeScope from "@/components/ThemeScope";
import { PRESETS } from "@/lib/themes";

const STATUS_LABEL = {
  new: "Baru",
  preparing: "Diproses",
  ready: "Siap",
  completed: "Selesai",
  cancelled: "Batal",
};

const PAY_LABEL = {
  unpaid: "Belum Bayar",
  pending: "Menunggu",
  paid: "Lunas",
  failed: "Gagal",
};

const HISTORY_PAGE_SIZE_KEY =
  "wm_customer_history_page_size";

export default function OrderHistory() {
  const [items, setItems] = useState(null);
  const [tokens, setTokens] = useState(
    PRESETS.midnight_noodle.tokens
  );

  const [size, setSize] = useState(() => {
    const saved = Number(
      localStorage.getItem(
        HISTORY_PAGE_SIZE_KEY
      )
    );

    return [10, 20, 50, 100].includes(saved)
      ? saved
      : 10;
  });

  const [page, setPage] = useState(1);

  useEffect(() => {
    http
      .get("/theme/public")
      .then(({ data }) =>
        setTokens(
          data.customer_theme?.tokens ||
            PRESETS.midnight_noodle.tokens
        )
      )
      .catch(() => {});

    http
      .get(
        `/orders/history/${guestToken()}`
      )
      .then(({ data }) => {
        const latest = [...(data.items || [])].sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        );

        setItems(latest);
      })
      .catch(() =>
        setItems([])
      );
  }, []);

  function changeSize(value) {
    setSize(value);
    setPage(1);

    localStorage.setItem(
      HISTORY_PAGE_SIZE_KEY,
      String(value)
    );
  }

  const totalPages = useMemo(() => {
    if (!items?.length) return 1;

    return Math.max(
      1,
      Math.ceil(items.length / size)
    );
  }, [items, size]);

  const pageItems = useMemo(() => {
    if (!items) return [];

    const start = (page - 1) * size;

    return items.slice(
      start,
      start + size
    );
  }, [items, page, size]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <ThemeScope
      tokens={tokens}
      className="min-h-dvh"
    >
      <div className="phone-frame min-h-dvh p-5">
        <div className="mb-5 flex items-center gap-3">
          <Link
            to="/t/DEMO4"
            className="grid h-10 w-10 place-items-center rounded-full bg-muted"
          >
            <ArrowLeft size={18} />
          </Link>

          <h1 className="font-heading text-2xl font-extrabold tracking-tight">
            Riwayat Pesanan
          </h1>
        </div>

        {items !== null &&
          items.length > 0 && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-3">
              <div>
                <div className="text-xs font-bold text-card-foreground">
                  Tampilkan
                </div>

                <div className="text-[10px] text-muted-foreground">
                  Jumlah pesanan per halaman
                </div>
              </div>

              <select
                value={size}
                onChange={(e) =>
                  changeSize(
                    Number(e.target.value)
                  )
                }
                className="rounded-xl border border-border bg-muted px-3 py-2 text-sm font-bold text-card-foreground outline-none"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / halaman
                  </option>
                ))}
              </select>
            </div>
          )}

        {items === null && (
          <div className="flex justify-center pt-16">
            <Loader2 className="wm-spin text-muted-foreground" />
          </div>
        )}

        {items?.length === 0 && (
          <div className="mt-16 text-center text-muted-foreground">
            <ReceiptText
              className="mx-auto mb-3"
              size={36}
            />

            <p className="font-heading text-lg">
              Belum ada pesanan
            </p>
          </div>
        )}

        <div className="space-y-3">
          {pageItems.map((o) => (
            <div
              key={o.id}
              data-testid={`history-${o.id}`}
              className="rounded-3xl border border-border bg-card p-4"
            >
              {/* HEADER ORDER */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <b className="font-heading text-sm text-card-foreground">
                    {o.id}
                  </b>

                  <div className="mt-1 text-xs text-muted-foreground">
                    {o.table_name} ·{" "}
                    {new Date(
                      o.created_at
                    ).toLocaleString(
                      "id-ID",
                      {
                        dateStyle:
                          "medium",
                        timeStyle:
                          "short",
                      }
                    )}
                  </div>
                </div>

                <b className="font-heading text-lg font-extrabold text-primary">
                  {rupiah(o.total)}
                </b>
              </div>

              {/* STATUS */}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold uppercase text-muted-foreground">
                  {STATUS_LABEL[
                    o.status
                  ] || o.status}
                </span>

                <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[10px] font-bold uppercase text-primary">
                  {o.payment_method}
                </span>

                <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-bold uppercase text-muted-foreground">
                  {PAY_LABEL[
                    o.payment_status
                  ] ||
                    o.payment_status}
                </span>
              </div>

              {/* DETAIL PESANAN */}
              {Array.isArray(
                o.items
              ) &&
                o.items.length >
                  0 && (
                  <div className="mt-4 space-y-3 border-t border-border pt-3">
                    {o.items.map(
                      (
                        item,
                        index
                      ) => {
                        const modifierTotal =
                          (
                            item.modifiers ||
                            []
                          ).reduce(
                            (
                              sum,
                              m
                            ) =>
                              sum +
                              (m.price_delta ||
                                0) *
                                (m.qty ||
                                  1),
                            0
                          );

                        const basePrice =
                          Math.max(
                            0,
                            (item.unit_price ||
                              0) -
                              modifierTotal
                          );

                        return (
                          <div
                            key={
                              index
                            }
                            className="rounded-2xl bg-muted/50 p-3"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-card-foreground">
                                  {
                                    item.qty
                                  }
                                  ×{" "}
                                  {
                                    item.name
                                  }
                                </div>

                                <div className="mt-0.5 text-[11px] text-muted-foreground">
                                  {rupiah(
                                    basePrice
                                  )}{" "}
                                  / item
                                </div>
                              </div>

                              <div className="text-right text-sm font-bold text-card-foreground">
                                {rupiah(
                                  basePrice *
                                    item.qty
                                )}
                              </div>
                            </div>

                            {(
                              item.modifiers ||
                              []
                            ).length >
                              0 && (
                              <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
                                {item.modifiers.map(
                                  (
                                    m,
                                    mi
                                  ) => (
                                    <div
                                      key={
                                        mi
                                      }
                                      className="flex items-center justify-between gap-3 text-xs"
                                    >
                                      <span className="text-muted-foreground">
                                        +{" "}
                                        {
                                          m.name
                                        }{" "}
                                        ×
                                        {m.qty ||
                                          1}
                                      </span>

                                      <span className="text-muted-foreground">
                                        {m.price_delta >
                                        0
                                          ? rupiah(
                                              m.price_delta *
                                                (m.qty ||
                                                  1)
                                            )
                                          : "Gratis"}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            )}

                            {item.note && (
                              <div className="mt-2 rounded-lg bg-background/40 px-2.5 py-2 text-[11px] italic text-muted-foreground">
                                Catatan:{" "}
                                {
                                  item.note
                                }
                              </div>
                            )}

                            <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2 text-xs">
                              <span className="font-semibold text-muted-foreground">
                                Total item
                              </span>

                              <span className="font-bold text-card-foreground">
                                {rupiah(
                                  item.line_total ||
                                    item.unit_price *
                                      item.qty
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}

              {/* TOTAL */}
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                <span className="text-sm font-bold text-muted-foreground">
                  Total Pesanan
                </span>

                <span className="font-heading text-lg font-extrabold text-primary">
                  {rupiah(
                    o.total
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        {items?.length > 0 &&
          totalPages > 1 && (
            <div className="mt-5 rounded-2xl border border-border bg-card p-3">
              <div className="mb-3 text-center">
                <div className="text-xs font-bold text-card-foreground">
                  Halaman {page} dari {totalPages}
                </div>

                <div className="text-[10px] text-muted-foreground">
                  {Math.min(
                    (page - 1) * size + 1,
                    items.length
                  )}
                  -
                  {Math.min(
                    page * size,
                    items.length
                  )}{" "}
                  dari {items.length} pesanan
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() =>
                    setPage((p) =>
                      Math.max(1, p - 1)
                    )
                  }
                  className="grid h-9 w-9 place-items-center rounded-xl bg-muted text-card-foreground transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
                  title="Halaman sebelumnya"
                >
                  <ChevronLeft size={17} />
                </button>

                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                )
                  .filter((n) => {
                    if (totalPages <= 7) return true;
                    if (n === 1 || n === totalPages) return true;
                    return Math.abs(n - page) <= 1;
                  })
                  .map((n, index, arr) => {
                    const prev = arr[index - 1];
                    const showDots =
                      prev && n - prev > 1;

                    return (
                      <div
                        key={n}
                        className="flex items-center gap-2"
                      >
                        {showDots && (
                          <span className="px-1 text-xs font-bold text-muted-foreground">
                            ...
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => setPage(n)}
                          className={`min-w-9 rounded-xl px-3 py-2 text-xs font-bold transition active:scale-95 ${
                            page === n
                              ? "bg-primary text-primary-foreground shadow-sm"
                              : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                          }`}
                        >
                          {n}
                        </button>
                      </div>
                    );
                  })}

                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() =>
                    setPage((p) =>
                      Math.min(
                        totalPages,
                        p + 1
                      )
                    )
                  }
                  className="grid h-9 w-9 place-items-center rounded-xl bg-muted text-card-foreground transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-35"
                  title="Halaman berikutnya"
                >
                  <ChevronRight size={17} />
                </button>
              </div>
            </div>
          )}
      </div>
    </ThemeScope>
  );
}